/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { EStationPricingOption } from '../../api/types';
import { IStation } from '../../api/station/types';
import MenuBlock from '../../components/menu-block/menu-block';
import setConnectorStyles from './components/set-connector/set-connector.styles';
import SetDuration from './components/set-duration/set-duration.component';
import SetEnergy from './components/set-energy/set-energy.component';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import ConnectorInfoContainer from './components/set-connector/ConnectorInfoContainer';
import {
    connectedConnectorStatuses,
    unavailableStationAndConnectorStatuses,
} from '../../constants';
import chevronLeft from '../../assets/icons/chevron_left.svg';
import SpinnerLoader from '../../components/spinner-loader/spinner-loader.component';
import {
    selectSetupModal,
    selectSetupModalConnector,
} from '../../store/setup-modal/setup-modal.selector';
import { setSetupModal } from '../../store/setup-modal/setup-modal.slice';
import { ISetUpState } from './set-up.types';
import { AnimationWrapper } from './components/AnimationWrapper';
import { getStationConnectorId, getStationCurrency, getStationLocationId, getStationLocationName, formatStationLocationAddress, buildStationConnectorPath, buildPayPath, isStationUnavailable } from '../../utils/station';
import { useOutletContext } from 'react-router-dom';
import { SecurityDeposit } from './components/security-deposit/security-deposit.component';
import { useCreateTransactionMutation } from '../../api/transactions/transactions.api';
import toast from 'react-hot-toast';
import { TariffDetails } from './components/tariff-details/tariff-details.component';

const energyPricingOptions = [EStationPricingOption.kwh, EStationPricingOption.free];

export const DEFAULT_CONNECTOR_ID = 1;

export const ConnectorModal = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { stationId, connectorId } = useParams();
    const { stationLoading, locationId: locationIdFromContext, station: stationFromContext } =
        useOutletContext<{
            stationLoading: boolean;
            locationId?: string;
            station?: IStation;
        }>();
    const [createTransaction] = useCreateTransactionMutation();
    const [isCreatingTransaction, setIsCreatingTransaction] = useState(false);

    const connectorFromStore = useSelector(selectSetupModalConnector);
    const connector = useMemo(() => {
        if (connectorFromStore) {
            return connectorFromStore;
        }

        if (!stationFromContext || !connectorId) {
            return undefined;
        }

        const foundConnector = stationFromContext.connectors?.find(c => c._id === connectorId);

        return foundConnector
            ? {
                  ...foundConnector,
                  station: stationFromContext,
              }
            : undefined;
    }, [connectorFromStore, stationFromContext, connectorId]);
    const setUpState: ISetUpState = useSelector(selectSetupModal);
    const station = useMemo(
        () => stationFromContext ?? connector?.station,
        [stationFromContext, connector]
    );
    const locationId = locationIdFromContext ?? getStationLocationId(station);
    const locationName = getStationLocationName(station);
    const address = useMemo(
        () => formatStationLocationAddress(station?.stationLocation),
        [station?.stationLocation]
    );

    const intl = useIntl();
    const [showChargingLimit, setShowChargingLimit] = useState(false);
    const [tariffDay, setTariffDay] = useState<number>(((new Date().getDay() + 6) % 7) as number);

    const handlePressNavHeader = useCallback((event: any) => {
        event?.stopPropagation();
    }, []);

    const handleSetSetup = useCallback(
        async (state: ISetUpState) => {
            const savedPhone = localStorage.getItem('phone');
            const savedEmail = localStorage.getItem('email');
            if (savedPhone) {
                state.phone = savedPhone;
            }
            if (savedEmail) {
                state.email = savedEmail;
            }
            dispatch(setSetupModal(state));
            if (state.phone && stationId && setUpState?.connector?._id) {
                const language = localStorage.getItem('language') || 'en';
                try {
                    setIsCreatingTransaction(true);
                    const connectorId = getStationConnectorId(station, setUpState?.connector?._id);
                    const result = await createTransaction({
                        phone: state.phone,
                        stationId,
                        language: language === 'ua' ? 'uk' : language,
                        ...(state?.energy ? { energy: state?.energy * 1000 } : null),
                        ...(state?.duration ? { duration: state?.duration } : null),
                        ...(savedEmail ? { email: savedEmail } : null),
                        ...(state?.energy || state?.duration ? null : { disableSessionLimit: true }),
                        connectorId: connectorId ?? DEFAULT_CONNECTOR_ID,
                    });
                    if (result?.data?._id) {
                        navigate(buildPayPath(result.data._id));
                    } else {
                        setIsCreatingTransaction(false);
                        toast.error(intl.formatMessage({ id: 'toast.error.checkConnector' }));
                    }
                } catch (error) {
                    setIsCreatingTransaction(false);
                    console.error('set error', error);
                    toast.error(intl.formatMessage({ id: 'toast.error.checkConnector' }));
                }
            } else if (connector) {
                navigate(
                    buildStationConnectorPath(
                        connector.station._id,
                        connector._id,
                        'confirm-details'
                    )
                );
            }
        },
        [dispatch, connector, setUpState, stationId, navigate, station, intl, createTransaction]
    );

    const connectorTariff = (connector as any)?.tariff;
    const isFree = connectorTariff?.pricingOption === EStationPricingOption.free;

    const isCarConnected =
        !!connector?.status && connectedConnectorStatuses.includes(connector.status);

    const isUnavailable =
        (!!connector?.status &&
            unavailableStationAndConnectorStatuses.includes(connector.status)) ||
        isStationUnavailable(connector?.station) ||
        connector?.mode === 'Inoperative';

    const isLoading = stationLoading || !station || !connector;

    const currency = getStationCurrency(station);

    const hasSecurityDeposit = !!connectorTariff?.securityDepositAmount;

    return (
        <div css={setConnectorStyles.energyTimeSelectorContainer}>
            <MenuBlock station={station} style={{ zIndex: 10002 }} />
            <div css={setConnectorStyles.energyTimeSelectorInnecBlock}>
                <NavLink
                    css={setConnectorStyles.goBackLink}
                    onClick={handlePressNavHeader}
                    to={locationId ? `/locations/${locationId}` : '/'}>
                    <img css={setConnectorStyles.goBackIcon} src={chevronLeft} alt="go back" />
                    <div css={setConnectorStyles.addressBlock}>
                        <span css={setConnectorStyles.locationName}>{locationName}</span>
                        <span css={setConnectorStyles.address}>{address}</span>
                    </div>
                </NavLink>
                <AnimationWrapper>
                    <div css={setConnectorStyles.energyTimeSelectorBlock}>
                        {isLoading ? (
                            <div css={setConnectorStyles.energyTimeSelectorBlockLoader}>
                                <SpinnerLoader />
                            </div>
                        ) : (
                            <>
                                <ConnectorInfoContainer
                                    isUnavailable={isUnavailable}
                                    connector={connector as any}
                                    isFree={isFree}
                                    station={station}
                                    showIdleFeeInfo
                                    connectorIdentifier={
                                        station?.stationLocation?.connectorIdentifier
                                    }
                                />
                                {hasSecurityDeposit && !showChargingLimit && (
                                    <SecurityDeposit
                                        showChargingLimit={showChargingLimit}
                                        setShowChargingLimit={setShowChargingLimit}
                                        isCarConnected={isCarConnected}
                                        onSetSetUpStep={handleSetSetup}
                                        amount={connectorTariff?.securityDepositAmount}
                                        currency={currency}
                                        isCreatingTransaction={isCreatingTransaction}
                                        isUnavailable={isUnavailable}
                                    />
                                )}
                                {(!hasSecurityDeposit || showChargingLimit) &&
                                    connector?.tariff?.pricingOption &&
                                    energyPricingOptions.includes(
                                        connectorTariff?.pricingOption
                                    ) && (
                                        <SetEnergy
                                            showChargingLimit={showChargingLimit}
                                            setShowChargingLimit={setShowChargingLimit}
                                            hasSecurityDeposit={hasSecurityDeposit}
                                            isFree={isFree}
                                            priceKW={connectorTariff?.priceForKwh ?? 0}
                                            currency={currency}
                                            onSetSetUpStep={handleSetSetup}
                                            isCarConnected={isCarConnected}
                                            securityDepositAmount={
                                                connectorTariff?.securityDepositAmount
                                            }
                                            isCreatingTransaction={isCreatingTransaction}
                                            isUnavailable={isUnavailable}
                                        />
                                    )}
                                {(!hasSecurityDeposit || showChargingLimit) &&
                                    connectorTariff?.pricingOption ===
                                        EStationPricingOption.hour && (
                                        <SetDuration
                                            showChargingLimit={showChargingLimit}
                                            setShowChargingLimit={setShowChargingLimit}
                                            hasSecurityDeposit={hasSecurityDeposit}
                                            isFree={isFree}
                                            priceHour={connectorTariff?.priceForHour ?? 0}
                                            currency={currency}
                                            onSetSetUpStep={handleSetSetup}
                                            isCarConnected={isCarConnected}
                                            securityDepositAmount={
                                                connectorTariff?.securityDepositAmount
                                            }
                                            isCreatingTransaction={isCreatingTransaction}
                                            isUnavailable={isUnavailable}
                                        />
                                    )}

                                {connectorTariff && (
                                    <TariffDetails
                                        tariff={connectorTariff}
                                        currency={currency}
                                        day={tariffDay}
                                        onDayChange={setTariffDay}
                                        disableSessionLimit={!showChargingLimit}
                                        minimalChargeAmount={station?.minimalChargeAmount}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </AnimationWrapper>
            </div>
        </div>
    );
};
